import { createFileRoute, Link } from "@tanstack/react-router";
import { clampDescription } from "@/lib/seo-quality";
import { Suspense } from "react";
import { ArrowUpRight, ArrowRight, Phone, Search, ShoppingBag, CheckCircle2, Shield, Truck, Wrench, Sparkles, MapPin, Calendar } from "lucide-react";
import { FyndanuGrid, FyndanuGridSkeleton, fyndanuListingsQuery } from "@/components/FyndanuGrid";
import { blocketVehiclesQuery } from "@/lib/vehicles";
import { SoldTodayBadge } from "@/components/SoldTodayBadge";
import { AdSlot } from "@/components/AdSlot";

const CANONICAL = "https://www.akhusbilfritid.se/fordon";
const TODAY = new Date().toISOString().slice(0, 10);

export const Route = createFileRoute("/fordon")({
  loader: async ({ context }) => {
    const [fyndanu, blocket] = await Promise.all([
      context.queryClient.ensureQueryData(fyndanuListingsQuery),
      context.queryClient.ensureQueryData(blocketVehiclesQuery).catch(() => ({ items: [] })),
    ]);
    return {
      items: [...(fyndanu?.items ?? []), ...(blocket?.items ?? [])],
    };
  },
  head: ({ loaderData }) => {
    const items = loaderData?.items ?? [];
    const count = items.length;
    const prices = items
      .map((i: { price?: number | null }) => i.price)
      .filter((p): p is number => typeof p === "number" && p >= 20000);

    const minPrice = prices.length ? Math.min(...prices) : null;
    // Sökintentionen är "husbilar kalix" / "husvagnar kalix" — orten först i titeln,
    // antal som klickutlösare, och pris + kontroller i beskrivningen.
    const title = `Husbilar & husvagnar till salu i Kalix${count > 0 ? ` – ${count} i lager` : ""}`;
    const description = clampDescription(
      `${count > 0 ? `${count} ` : ""}husbilar och husvagnar till salu hos AK Husbil & Fritid i Kalix.` +
        (minPrice ? ` Priser från ${minPrice.toLocaleString("sv-SE")} kr.` : "") +
        " Fukttestade & gasoltestade. Ring 070-862 97 43.",
    );


    return {
      meta: [
        { title },
        { name: "description", content: description },
        { name: "keywords", content: "husbil Kalix, husvagn Kalix, fordonslager Kalix, begagnad husbil Norrbotten, Rapido återförsäljare Norrbotten" },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:url", content: CANONICAL },
        { property: "og:type", content: "website" },
        { property: "og:locale", content: "sv_SE" },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: title },
        { name: "twitter:description", content: description },
      ],
      links: [{ rel: "canonical", href: CANONICAL }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@graph": [
              {
                "@type": "BreadcrumbList",
                itemListElement: [
                  { "@type": "ListItem", position: 1, name: "Hem", item: "https://www.akhusbilfritid.se/" },
                  { "@type": "ListItem", position: 2, name: "Husbilar & husvagnar till salu", item: CANONICAL },
                ],
              },
              {
                "@type": "CollectionPage",
                "@id": CANONICAL + "#collection",
                url: CANONICAL,
                name: "Husbilar & husvagnar till salu",
                description,
                dateModified: TODAY,
                inLanguage: "sv-SE",
                isPartOf: { "@id": "https://www.akhusbilfritid.se/#website" },
                about: [
                  { "@type": "Thing", name: "Husbilar till salu" },
                  { "@type": "Thing", name: "Husvagnar till salu" },
                  { "@type": "Thing", name: "Begagnade husbilar" },
                ],
                mainEntity: {
                  "@type": "ItemList",
                  numberOfItems: count,
                  itemListOrder: "https://schema.org/ItemListOrderAscending",
                  name: "Aktuella husbilar och husvagnar i lager",
                },
              },
              {
                "@type": "AutoDealer",
                "@id": "https://www.akhusbilfritid.se/#dealer",
                name: "AK Husbil & Fritid",
                url: "https://www.akhusbilfritid.se/",
                telephone: "+46708629743",
                priceRange: "$$-$$$",
                areaServed: ["Kalix", "Luleå", "Piteå", "Boden", "Haparanda", "Norrbotten", "Norrland", "Sverige"],
                address: {
                  "@type": "PostalAddress",
                  addressLocality: "Kalix",
                  addressRegion: "Norrbotten",
                  addressCountry: "SE",
                },
                makesOffer: [
                  { "@type": "Offer", itemOffered: { "@type": "Service", name: "Husbilar till salu" } },
                  { "@type": "Offer", itemOffered: { "@type": "Service", name: "Husvagnar till salu" } },
                  { "@type": "Offer", itemOffered: { "@type": "Service", name: "Rapido husbilar – officiell återförsäljare" } },
                ],
              },
              {
                "@type": "FAQPage",
                mainEntity: [
                  {
                    "@type": "Question",
                    name: "Hur många husbilar och husvagnar har ni till salu just nu?",
                    acceptedAnswer: {
                      "@type": "Answer",
                      text: `Vi har ${count > 0 ? count + " " : ""}genomgångna husbilar och husvagnar i lager i Kalix. Utbudet uppdateras dagligen och syns live både här på akhusbilfritid.se, på Fyndanu och på Blocket.`,
                    },
                  },
                  {
                    "@type": "Question",
                    name: "Är fordonen fukttestade och gasoltestade?",
                    acceptedAnswer: {
                      "@type": "Answer",
                      text: "Ja. Varje husbil och husvagn vi säljer är fukttestad och gasoltestad vid leverans. Vi lämnar aldrig ut ett fordon utan protokoll — det står vi för skriftligt.",
                    },
                  },
                  {
                    "@type": "Question",
                    name: "Kan ni leverera husbil eller husvagn i hela Sverige?",
                    acceptedAnswer: {
                      "@type": "Answer",
                      text: "Ja, vi levererar husbilar och husvagnar i hela Sverige — från Kalix till Skåne. Många kunder kombinerar hämtning i Norrbotten med en semestertur hem. Vi ordnar även transport när det passar bättre.",
                    },
                  },
                  {
                    "@type": "Question",
                    name: "Går det att finansiera köpet?",
                    acceptedAnswer: {
                      "@type": "Answer",
                      text: "Ja. Vi samarbetar med etablerade fordonsfinansiärer och kan förbereda finansiering på 15 minuter. Standard är 20 % kontant och 84 månaders löptid, men vi anpassar efter din situation.",
                    },
                  },
                  {
                    "@type": "Question",
                    name: "Tar ni in min gamla husbil eller husvagn i inbyte?",
                    acceptedAnswer: {
                      "@type": "Answer",
                      text: "Absolut. Skicka bilder och reg.nr till oss så får du en rak värdering samma dag. Vi tar in de flesta märken — Rapido, Kabe, Adria, Hobby, Cabby, Knaus, Fendt, Bürstner, Hymer och fler.",
                    },
                  },
                  {
                    "@type": "Question",
                    name: "Vilket körkort behöver jag för en husbil?",
                    acceptedAnswer: {
                      "@type": "Answer",
                      text: "Upp till 3 500 kg totalvikt räcker det med B-körkort. 3 500–4 250 kg kräver B96 eller BE. Över 4 250 kg krävs C1. Vi märker upp körkortsklassen på varje annons så du ser direkt vad som gäller.",
                    },
                  },
                ],
              },
            ],
          }),
        },
      ],
    };
  },
  component: FordonPage,
  errorComponent: ({ error }: { error: unknown }) => (
    <div className="container-edge py-20" role="alert">Något gick fel: {error instanceof Error ? error.message : String(error)}</div>
  ),
  notFoundComponent: () => <div className="container-edge py-20">Sidan hittades inte.</div>,
});

const marketplaces = [
  {
    n: "01",
    title: "Fyndanu",
    desc: "Vårt fullständiga utbud med detaljerade bilder, specifikationer och priser — alltid uppdaterat.",
    href: "https://www.fyndanu.se/butik/e435da0b-10d0-46fa-a389-702295c89840",
  },
  {
    n: "02",
    title: "Blocket",
    desc: "Vår butik på Sveriges största marknadsplats — bekvämt att jämföra och bevaka.",
    href: "https://www.blocket.se/butik/ak-husbil--fritid",
  },
];

const trustPoints = [
  { icon: CheckCircle2, title: "Fukttestad vid leverans", body: "Protokoll medföljer varje fordon — inga överraskningar sex månader senare." },
  { icon: Shield, title: "Gasoltestad & säkerhetscheckad", body: "Godkänd gasolanläggning enligt LPG-normen. Vi lämnar aldrig ut en anläggning som inte klarar tätheten." },
  { icon: Wrench, title: "Genomgången service", body: "Bromsar, hjullager, tak, tätningar och batterier kontrolleras innan fordonet får ligga ute på sajten." },
  { icon: Truck, title: "Leverans i hela Sverige", body: "Från Kalix till Skåne. Hämtning på plats eller körning hem — vi löser det som passar dig." },
  { icon: Sparkles, title: "Rapido officiell återförsäljare", body: "Enda auktoriserade Rapido-återförsäljaren i Norrbotten. Nya modeller, delar och service." },
  { icon: MapPin, title: "Norrlands mest hemtama handlare", body: "Familjeföretag i Kalix sedan flera år. Ring, mejla eller kom förbi — du får alltid tala med en säljare, inte ett callcenter." },
];

const cityLinks = [
  { label: "Husbil Kalix", to: "/husbil-kalix" as const },
  { label: "Husvagn Kalix", to: "/husvagn-kalix" as const },
  { label: "Husbil Luleå", to: "/husbil-lulea" as const },
  { label: "Husbil Piteå", to: "/husbil-pitea" as const },
  { label: "Husbil Boden", to: "/husbil-boden" as const },
  { label: "Husbil Haparanda", to: "/husbil-haparanda" as const },
  { label: "Husbil Norrbotten", to: "/husbil-norrbotten" as const },
];

export function PageHeader({ label, title, sub }: { label: string; title: React.ReactNode; sub?: string }) {
  return (
    <section className="bg-warm">
      <div className="container-edge pt-20 pb-14 md:pt-28 md:pb-20">
        <div className="flex flex-wrap items-center gap-3">
          <p className="eyebrow">{label}</p>
          <SoldTodayBadge variant="light" />
        </div>
        <h1 className="mt-4 max-w-4xl font-display text-5xl leading-[1.02] tracking-tight md:text-7xl lg:text-8xl">
          {title}
        </h1>
        {sub && (
          <p className="mt-7 max-w-2xl text-lg leading-relaxed text-muted-foreground md:text-xl">
            {sub}
          </p>
        )}
      </div>
    </section>
  );
}

function FordonPage() {
  const updated = new Date().toLocaleDateString("sv-SE", { year: "numeric", month: "long", day: "numeric" });

  return (
    <>
      <PageHeader
        label="Husbilar & husvagnar till salu · Uppdateras dagligen"
        title={<>Husbilar & husvagnar <span className="font-italic-display text-accent">till salu</span>.</>}
        sub="Genomgångna, fukttestade och gasoltestade fordon i Kalix — raka priser, ingen hemlig prislapp. Se hela lagret live, filtrera på Fyndanu eller Blocket, eller ring direkt så letar vi tillsammans."
      />

      <section className="container-edge -mt-6 pb-4">
        <div className="flex flex-wrap gap-3">
          <a href="https://www.fyndanu.se/butik/e435da0b-10d0-46fa-a389-702295c89840" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 btn-shine rounded-full bg-primary px-5 py-3 text-sm font-semibold text-primary-foreground transition hover:bg-primary/90">
            <Search size={16} /> Se hela lagret
          </a>
          <a href="tel:0708629743" className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-5 py-3 text-sm font-semibold transition hover:bg-secondary">
            <Phone size={16} /> Ring 070-862 97 43
          </a>
          <Link to="/kontakt" className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-5 py-3 text-sm font-semibold transition hover:bg-secondary">
            Fråga om ett fordon <ArrowRight size={14} />
          </Link>
          <Link to="/husbil-matchning" className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-5 py-3 text-sm font-semibold transition hover:bg-secondary">
            Matcha rätt fordon <Sparkles size={14} />
          </Link>
        </div>
        <p className="mt-4 inline-flex items-center gap-2 text-xs text-muted-foreground">
          <Calendar size={12} /> Uppdaterad {updated}
        </p>
      </section>

      <section className="container-edge pt-14 pb-6">
        <div className="mb-8 flex items-end justify-between gap-6">
          <div>
            <p className="eyebrow">Aktuella fordon · Live från Fyndanu & Blocket</p>
            <h2 className="mt-3 font-display text-3xl md:text-5xl leading-[1.05]">
              Allt vi har <span className="font-italic-display text-accent">just nu</span>.
            </h2>
          </div>
          <a
            href="https://www.fyndanu.se/butik/e435da0b-10d0-46fa-a389-702295c89840"
            target="_blank"
            rel="noopener noreferrer"
            className="hidden md:inline-flex items-center gap-2 text-sm font-semibold text-accent hover:underline underline-offset-4"
          >
            Öppna butiken <ArrowUpRight size={16} />
          </a>
        </div>
        <Suspense fallback={<FyndanuGridSkeleton />}>
          <FyndanuGrid />
        </Suspense>
        {/* En annons först efter att man scrollat igenom hela lagret */}
        <AdSlot variant="display-horizontal" label="Annons" />
      </section>


      {/* Split-links: dedicated inventory pages – strong internal linking for money keywords */}
      <section className="container-edge py-14">
        <p className="eyebrow">Bläddra efter fordonstyp</p>
        <div className="mt-6 grid gap-6 md:grid-cols-2">
          <Link to="/husbilar-till-salu" className="group rounded-3xl border border-border bg-card p-8 hover-lift">
            <h2 className="font-display text-3xl md:text-4xl">Husbilar till salu</h2>
            <p className="mt-3 text-muted-foreground">Alla husbilar i lager — halvintegrerade, integrerade, alkovmodeller och kompakta van. Filtrera på sovplatser, körkort och budget.</p>
            <span className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-accent group-hover:underline underline-offset-4">Se husbilarna <ArrowRight size={14} /></span>
          </Link>
          <Link to="/husvagnar-till-salu" className="group rounded-3xl border border-border bg-card p-8 hover-lift">
            <h2 className="font-display text-3xl md:text-4xl">Husvagnar till salu</h2>
            <p className="mt-3 text-muted-foreground">Genomgångna husvagnar — Kabe, Adria, Hobby, Cabby, Knaus och fler. Familjevagnar, vintervagnar och kompakta modeller.</p>
            <span className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-accent group-hover:underline underline-offset-4">Se husvagnarna <ArrowRight size={14} /></span>
          </Link>
        </div>
      </section>

      {/* Trust / why-us — E-E-A-T signal for Google */}
      <section className="bg-warm">
        <div className="container-edge py-16 md:py-20">
          <p className="eyebrow">Därför köper norrlänningar husbil hos oss</p>
          <h2 className="mt-3 max-w-3xl font-display text-3xl md:text-5xl leading-[1.05]">
            Ingen husbil eller husvagn <span className="font-italic-display text-accent">lämnar tomten</span> utan protokoll.
          </h2>
          <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {trustPoints.map((t) => (
              <div key={t.title} className="rounded-2xl border border-border bg-card p-6">
                <t.icon size={22} className="text-accent" />
                <h3 className="mt-4 font-display text-xl">{t.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{t.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Buying guide – long-form editorial content boosts topical authority */}
      <section className="container-edge py-16 md:py-20">
        <div className="grid gap-12 lg:grid-cols-[1fr_1.4fr]">
          <div>
            <p className="eyebrow">Så köper du</p>
            <h2 className="mt-3 font-display text-3xl md:text-5xl leading-[1.05]">
              Från kontakt till <span className="font-italic-display text-accent">rulla hem</span>.
            </h2>
            <p className="mt-6 text-muted-foreground">Att köpa husbil eller husvagn ska inte vara krångligt. Här är vår raka process — samma för alla, oavsett om du bor i Kalix eller Kristianstad.</p>
          </div>
          <ol className="space-y-6">
            {[
              { n: "01", t: "Hitta fordonet", b: "Bläddra i lagret ovan, filtrera på Fyndanu eller använd vår matchningsguide. Har du frågor — ring 070-862 97 43." },
              { n: "02", t: "Provkör i Kalix eller video", b: "Kom förbi för provkörning. Bor du långt bort? Vi filmar detaljerade genomgångar och ringer FaceTime så du får se allt i realtid." },
              { n: "03", t: "Byte, finansiering & kontrakt", b: "Vi värderar din gamla husbil samma dag och förbereder finansiering på cirka 15 minuter. Kontrakt digitalt eller på plats." },
              { n: "04", t: "Fukt- & gasoltest före leverans", b: "Innan du får nycklarna görs full fukt- och gasolkontroll. Protokoll skrivs under av oss och lämnas med i pärmen." },
              { n: "05", t: "Hämta eller få hemlevererat", b: "Kom och hämta — eller så kör vi ut fordonet till dig. Många väljer att kombinera hämtning med en semestervecka i Norrbotten." },
            ].map((s) => (
              <li key={s.n} className="grid grid-cols-[auto_1fr] gap-5 rounded-2xl border border-border bg-card p-6">
                <span className="font-display text-3xl text-accent">{s.n}</span>
                <div>
                  <h3 className="font-display text-xl">{s.t}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{s.b}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* City internal links — local SEO signal cluster */}
      <section className="bg-warm">
        <div className="container-edge py-14">
          <p className="eyebrow">Husbilar i norra Sverige</p>
          <h2 className="mt-3 font-display text-3xl md:text-4xl">Vi levererar i hela Norrland — och Sverige.</h2>
          <div className="mt-6 flex flex-wrap gap-3">
            {cityLinks.map((c) => (
              <Link key={c.to} to={c.to} className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-4 py-2 text-sm font-medium hover:bg-secondary">
                {c.label} <ArrowRight size={12} />
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ – matches FAQPage schema above */}
      <section className="container-edge py-16 md:py-20">
        <p className="eyebrow">Vanliga frågor</p>
        <h2 className="mt-3 font-display text-3xl md:text-5xl leading-[1.05]">
          Frågor vi får <span className="font-italic-display text-accent">varje vecka</span>.
        </h2>
        <div className="mt-10 divide-y divide-border rounded-2xl border border-border bg-card">
          {[
            ["Är fordonen fukttestade och gasoltestade?", "Ja. Varje husbil och husvagn vi säljer är fukttestad och gasoltestad vid leverans. Protokoll medföljer — vi lämnar aldrig ut ett fordon utan."],
            ["Kan ni leverera i hela Sverige?", "Ja, från Kalix till Skåne. Antingen kör vi ut fordonet till dig, eller så kombinerar du hämtning med en semestervecka i Norrbotten. Ring så räknar vi ut det som passar bäst."],
            ["Går det att finansiera köpet?", "Ja. Vi samarbetar med etablerade fordonsfinansiärer och förbereder finansiering på cirka 15 minuter. Standard är 20 % kontant och 84 månaders löptid, men vi anpassar."],
            ["Tar ni in min gamla husbil eller husvagn i inbyte?", "Absolut. Skicka bilder och reg.nr så får du en rak värdering samma dag. Vi tar in Rapido, Kabe, Adria, Hobby, Cabby, Knaus, Fendt, Bürstner, Hymer och fler."],
            ["Vilket körkort behöver jag för en husbil?", "Upp till 3 500 kg räcker B-körkort. 3 500–4 250 kg kräver B96/BE. Över 4 250 kg krävs C1. Vi märker upp körkortsklassen på varje annons."],
            ["Hur ofta uppdateras lagret?", "Vi synkar Fyndanu och Blocket två gånger om dygnet. Nya fordon och prisändringar syns här inom några timmar."],
          ].map(([q, a]) => (
            <details key={q} className="group p-6">
              <summary className="cursor-pointer list-none font-display text-lg md:text-xl">
                <span className="inline-flex items-center gap-3">
                  <span className="text-accent">+</span> {q}
                </span>
              </summary>
              <p className="mt-3 pl-6 text-sm leading-relaxed text-muted-foreground">{a}</p>
            </details>
          ))}
        </div>
      </section>

      <section className="container-edge pb-20">
        <p className="eyebrow">Välj marknadsplats</p>
        <div className="mt-6 grid gap-6 md:grid-cols-2">
          {marketplaces.map((l) => (
            <a
              key={l.href}
              href={l.href}
              target="_blank"
              rel="noopener noreferrer"
              className="group relative block rounded-3xl border border-border bg-card p-9 hover-lift"
            >
              <div className="flex items-start justify-between">
                <span className="font-display text-5xl text-accent">{l.n}</span>
                <ArrowUpRight size={28} className="text-muted-foreground transition group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-foreground" />
              </div>
              <h2 className="mt-10 font-display text-4xl">{l.title}</h2>
              <p className="mt-3 text-base leading-relaxed text-muted-foreground">{l.desc}</p>
              <p className="mt-8 inline-flex items-center gap-2 text-xs font-medium uppercase tracking-wider text-accent">
                <ShoppingBag size={14} /> Öppna butik
              </p>
            </a>
          ))}
        </div>
      </section>
    </>
  );
}
